import { useState, useContext, useEffect, useRef } from "react";
import { Box, Tooltip, ClickAwayListener } from "@mui/material";
import { createSaleOrderLine } from "./../../../../database/helpers/sale_order";
import { AddQuickStockItem } from "./../../../common";
import { SaleBookNonIdeal } from "../../../common/NewNonIdeal";

import { Sync } from "../../../../SyncService";
import { LanguageContext } from "../../../../context/LanguageContext";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { AppPagination, ParagraphText, UBInput } from "../../../../Libraries";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import withObservables from "@nozbe/with-observables";
import { observeUnArchivedStock } from "../../../../database/helpers/stock";
import { useAuthContext } from "../../../../context/AuthContext";
import { analytics, triggerLogEvent } from "../../../../config/firebase";

import { useLocation, useNavigate } from "react-router-dom";
import { ITEMS_PER_PAGE, sentenceCase } from "../../../../constants";
import { useFiltersHandler } from "../../../../hooks/useFilterHandler";
import { useAllStockReport } from "../../../../hooks/useAllStockReport";
import {
  CloseIcon,
  NewPlusIcon,
  SearchIcon,
} from "../../../../assets/icons/icon";
import galleryPlaceholder from "../../../../assets/images/galleryPlaceholder.png";
import styles from "./stockItemList.module.css";
import NonIdealNew from "../../../common/NonIdealNew";

const StockItemList = ({
  onClickStockItem,
  isShowQuickStockForm,
  setIsShowQuickStockForm,
  activeSaleOrder,
  setValue,
  user,
}) => {
  const ITEMS_PER_PAGE = 30;
  const navigate = useNavigate();
  const location = useLocation()
  const [searchQuery, setSearchQuery] = useState("");
  const [stockName, setStockName] = useState("");
  const [I18n] = useContext(LanguageContext);
  const debounceSearchQuery = useDebouncedValue(searchQuery, 500);
  const [pageSize, setPageSize] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [justAddQuickItem, setJustAddQuickItem] = useState(false);

  const inputRef = useRef();
  const [page, setPage] = useState(0);

  const { business } = useAuthContext();
  const itemRefs = useRef({});

  const { filters } = useFiltersHandler(undefined, I18n);

  const {
    stockList: list,
    totalStockCount: totalCount,
    handleStock,
    isLoading,
    totalStockCount,
    totalAllStocks,
  } = useAllStockReport(business);

  useEffect(() => {
    setPageSize(0);
    handleStock({
      limit: ITEMS_PER_PAGE,
      offset: 0,
      filter: {
        ...filters,
        keywords: debounceSearchQuery,
      },
    });
  }, [debounceSearchQuery]);

  const handleSearchChange = (value) => {
    setPageSize(0);
    setSearchQuery(value);
  };
  const addQuickStockItem = async (itemDetail) => {
    try {
      let paramsForStock = {
        selling_price: Number(itemDetail.price),
        stock: 0,
        minimum_order_quantity: 0,
        manufacturing_date: null,
        name: itemDetail.name,
        unit_conversion: null,
        wholesale_price: null,
        secondary_unit: null,
        batch_number: null,
        expiry_alert: null,
        expiry_date: null,
        image_url: itemDetail.image_url || null,
        low_stock: null,
        unit: "Pieces",
        cost_price: 0,
        barcode: null,
        imei: null,
        inclusive: true,
      };
      let created_stock = await business.createStock(paramsForStock);

      let itemObj = {
        selling_price: paramsForStock.selling_price,
        cost_price: 0,
        updated_unit: paramsForStock.unit,
        discount_method: "rs",
        name: paramsForStock.name,
        discount: null,
        quantity: 1,
        id: created_stock.id,
      };
      await createSaleOrderLine(itemObj, activeSaleOrder);
      setPageSize(0);
      handleStock({
        limit: ITEMS_PER_PAGE,
        offset: 0,
      });
      setTimeout(() => {
        setJustAddQuickItem(false);
      }, 0);
      Sync(user.phone_number, navigate);
      triggerLogEvent(analytics, "pos_stock_created_from_search");
    } catch (e) {
      throw e;
    }
  };

  const readyForCreateNewItemInStock = () => {
    setStockName(searchQuery);
    setSearchQuery("");
    setIsShowQuickStockForm(true);
  };
  useEffect(() => {
    const handleKeyDown = (event) => {
      const activeElement = document.activeElement;
      const tagName = activeElement.tagName.toLowerCase();
      const isContentEditable = activeElement.isContentEditable;
      if (!list.length) return;

      if (!event.shiftKey && event.key === "Enter" && !justAddQuickItem) {
        event.preventDefault();
        if (activeIndex === -1) {
          // No active item yet, activate first item
          setActiveIndex(0);
          inputRef?.current?.blur();
          if (itemRefs?.current[list[0].id])
            itemRefs?.current[list[0].id].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
        } else {
          // Call function on active item
          onClickStockItem(list[activeIndex]);
          inputRef?.current?.blur();
        }
      }

      if (!event.shiftKey && event.key === "Escape") {
        event.preventDefault();
        inputRef?.current?.blur();
        setActiveIndex(-1);
      }
      if (tagName === "input" || tagName === "textarea" || isContentEditable) {
        return;
      }
      if (event.shiftKey && event.key.toLowerCase() === "f") {
        event.preventDefault();
        inputRef?.current?.focus();
      }
    };
    const handleKeyUp = (event) => {
      if (isLoading) {
        return;
      }
      if (event.key === "ArrowDown" && activeIndex != -1) {
        event.preventDefault();
        setActiveIndex((prev) => (prev < totalStockCount - 1 ? prev + 1 : 0));
      }
      if (event.key === "ArrowUp" && activeIndex != -1) {
        event.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalStockCount - 1));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [activeIndex, list, onClickStockItem, isLoading]);

  useEffect(() => {
    if (itemRefs?.current[list[activeIndex]?.id])
      itemRefs.current[list[activeIndex].id].scrollIntoView({
        behavior: "auto",
        block: "center",
      });
  }, [activeIndex]);

  return (
    <ClickAwayListener onClickAway={() => setActiveIndex(-1)}>
      <Box className={styles.container}>
        <Box className={styles.header}>
          {totalAllStocks > 0 ? (
            <UBInput
              inputId="invoice_list-search"
              placeholder={I18n.search_item}
              onChange={(_, value) => handleSearchChange(value)}
              name="stockSearch"
              value={searchQuery}
              onFocus={() => setIsShowQuickStockForm(false)}
              maxLength={70}
              inputRef={inputRef}
              prefix={<SearchIcon />}
              suffix={
                searchQuery ? <CloseIcon className="cursorPointer" /> : null
              }
              handleSuffix={() => {
                handleSearchChange("");
                inputRef?.current?.focus();
              }}
              baseClassname="stockSearchInput"
            />
          ) : null}
        </Box>

        {totalAllStocks > 0 ? (
          <Box
            className={`${styles.scrollArea} generic-scrollbar`}
            id="scrollableDiv"
          >
            <Box className={styles.scrollAreaContent}>
              {!isShowQuickStockForm && list?.length > 0 && (
                <Box
                  onClick={() => { triggerLogEvent(analytics, "pos_quick_stock_form_open"); setIsShowQuickStockForm(true); }}
                  className={styles.quickItemContainer}
                >
                  <NewPlusIcon width={20} height={20} />
                  <ParagraphText className="fontSize14 fontWeight400 newFontColor">
                    {I18n.add_quick_item}
                  </ParagraphText>
                </Box>
              )}

              <Box className={styles.stickyForm}>
                <AddQuickStockItem
                  onSubmit={(itemDetails) => {
                    setJustAddQuickItem(true);
                    addQuickStockItem(itemDetails);
                  }}
                  isShowQuickStockForm={isShowQuickStockForm}
                  setIsShowQuickStockForm={setIsShowQuickStockForm}
                  setStockName={setStockName}
                  stockName={stockName}
                />
              </Box>

              {!(list.length > 0) && searchQuery && (
                <Box className={styles.quickItemContainer}>
                  <ParagraphText
                    className={styles.createItemStyle}
                    id="pos_create_new_stock"
                    onClick={readyForCreateNewItemInStock}
                  >
                    <NewPlusIcon width={20} height={20} />
                    {I18n.create} "{searchQuery}" {I18n.as_new_item}
                  </ParagraphText>
                </Box>
              )}

              {list.length > 0 && (
                <AppPagination
                  data={{
                    handleNext: () => {
                      if (list.length < totalCount) {
                        setPageSize((value) => value + 20);
                        handleStock({
                          limit: ITEMS_PER_PAGE,
                          offset: pageSize + 20,
                          isCalculateTotal: false,
                        });
                      }
                    },
                    pageLoad: isLoading,
                    count: totalCount,
                    list,
                    scrollableTarget: "scrollableDiv",
                  }}
                >
                  {list.map((product, index) => (
                    <Box
                      key={index}
                      onClick={() => onClickStockItem(product)}
                      ref={(el) => (itemRefs.current[product.id] = el)}
                      className={`${styles.stockPerItem}`}
                      sx={{
                        background:
                          index === activeIndex
                            ? "var(--newSecondaryColor)"
                            : "",
                      }}
                    >
                      <Box className={styles.nameBox}>
                        <Box
                          className={`${styles.productImageBox}${
                            product?.image_url
                              ? ""
                              : ` ${styles.productPlaceholder}`
                          }`}
                        >
                          {product?.image_url ? (
                            <img
                              src={product.image_url}
                              width="100%"
                              height="100%"
                              alt=""
                            />
                          ) : (
                            <img
                              src={galleryPlaceholder}
                              alt=""
                              width="100%"
                              height="100%"
                            />
                          )}
                        </Box>

                        <Tooltip title={product.name} arrow>
                          <div className="truncate stockName">
                            {sentenceCase(product.name)}
                          </div>
                        </Tooltip>
                      </Box>

                      <div className={styles.priceBox}>
                        Rs. {formatNum(product.selling_price)}
                      </div>
                    </Box>
                  ))}
                </AppPagination>
              )}
            </Box>
          </Box>
        ) : totalAllStocks === 0 && !isLoading ? (
          <NonIdealNew
            title={I18n.start_selling}
            subtitle={I18n.create_stock_book_item}
            button1Title="sell_open_item"
            onButton1Click={() => setValue(1)}
            loading={false}
            disabledOne={false}
            buttonOneId="sell_open_item"
            listButton2Title="create_item"
            onListButton2Click={() => navigate(`/Inventory/Create`,{
              state : {
                pathname : location.pathname,
              }
            })}
            listButtonTwoId="create_item"
            isTransparentButtonOne={true}
          />
        ) : null}
      </Box>
    </ClickAwayListener>
  );
};

const enhance = withObservables([], () => {
  return {
    stock_list: observeUnArchivedStock(localStorage.getItem("business_id")),
  };
});

export default enhance(StockItemList);
